import React, { FC, useEffect, useRef, useState } from "react";
import { PropsWithChildren } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Socket } from "socket.io-client";

import { patientTableHeadData } from "../hooks/patients/staticData";
import {
  CustomTableRow,
  Table,
  TableBody,
  TableButtons,
  TableHead,
  TableNoContent,
  TableStatusWithToggle,
  Pagination,
} from "../components/NewTable";
import DatePicker from "../components/DatePicker";
import TableSkeleton from "../Skeletons/TableSkeleton";
import AccordionRow from "../components/AccordionRow";
import { IPatient } from "../hooks/patients/index.type";
import SelectClinicianModal from "../pageComponents/Patients/SelectClinicianModal";
import ChatModal from "../pageComponents/Patients/ChatModal";
import { inputPhoneMasking } from "../utils/validators";
import {
  decryptPrivateKeyBackup,
  extractNumbersFromId,
} from "../utils/generateKey";
import { formatDateToMMDDYYYY } from "../utils/auditDates";

import { AddPatientModalState } from "../pageComponents/Patients/AddPatientModal";
import {
  useAllPatient,
  useFetchStatus,
  useFetchAssociatedClinic,
} from "../hooks/patients";
import { loginSliceValue } from "../redux/reducers/loginSlice";
import { Content } from "../components";
import ListHeader from "../components/ListHeader/ListHeader";
import ActiveToggleModal from "../pageComponents/Patients/ActiveToggleModal";
import CheckboxInput from "../components/Modal/CheckBoxInput";
import { IAddNoteStates } from "../pageComponents/Patients/AddNotesModal";

import {
  useFetchTransmission,
  useGetScheduleDateFilter,
  useUpdatePhoneCallReminderDate,
  useUpdatePhoneCallStatus,
  useUpdateCallStatus,
} from "../hooks/patients";
import useFetchNotes from "../hooks/patients/useFetchNotes";
import useFetchBillingCycleDaysLeft from "../hooks/billingList/useFetchBillingCycleDaysLeft";
import useFetchIntakeFormStatus from "../hooks/patients/useFetchIntakeFormStatus";
import { useEncryptedChat } from "../hooks/chats";
import useFetchClinicians from "../hooks/patients/useFetchClinicians";
import EnrollPatientModal from "../pageComponents/Patients/EnrollPatientModal";
import { clearRescivedMessage } from "../redux/reducers/onlineUsers";
import useAddNote from "../hooks/patients/useAddNote";
import PreviewModalVersion2 from "../pageComponents/Patients/PreviewModalVersion2";
import useGetAlerts from "../hooks/patients/useGetAlerts";
import useFloatingTimer, {
  TimerResponse,
} from "../hooks/patients/useFloatingTimer";
import FloatingTimer from "../components/AccordionRow/FloatingTimer";

import styles from "../styles/Patient.module.css";
import classes from "../styles/NewTable.module.css";
import { formatDateForCallSchedule } from "../utils/date";
import RadioDropdown from "../components/RadioDropdown";

export interface IChatModalState {
  status?: boolean | undefined;
  data?: IPatient;
}

export type IntakeFormQuestion = {
  question_id?: string;
  question_answer?: string | string[] | number;
  question_type?: "single_option" | "multiple_option" | "scale" | "textarea";
};

export type IaddPatientModalStates = PropsWithChildren<{
  name: { value: string; error: string };
  dobString: { value: string; error: string };
  gender: { value: string; error: string };
  phone: { value: string; error: string };
  email: { value: string; error: string };
  address1: { value: string; error: string };
  address2: { value: string; error: string };
  state: { value: string; error: string };
  city: { value: string; error: string };
  zip: { value: string; error: string };
  associatedClinic: { value: string; error: string };
  billingProvider: { value: string; error: string };
  bestTimeToContact: { value: string; error: string };
  primaryInsurance: {
    provider: { value: string; error: string };
    policyNumber: { value: string; error: string };
    groupNumber: { value: string; error: string };
  };
  secondaryInsurance: {
    provider: { value: string; error: string };
    policyNumber: { value: string; error: string };
    groupNumber: { value: string; error: string };
  };
  rtmSelfPay: { value: "yes" | "no"; error: string };
  rtmRedFlag: { value: "yes" | "no"; error: string };
  rtmRedFlagReason: { value: string; error: string };
}>;

export const startaddPatientModalStates = {
  name: { value: "", error: "" },
  dobString: { value: "", error: "" },
  gender: { value: "", error: "" },
  email: { value: "", error: "" },
  phone: { value: "", error: "" },
  associatedClinic: { value: "", error: "" },
  billingProvider: { value: "", error: "" },
  bestTimeToContact: { value: "", error: "" },
  primaryInsurance: {
    provider: { value: "", error: "" },
    policyNumber: { value: "", error: "" },
    groupNumber: { value: "", error: "" },
  },
  secondaryInsurance: {
    provider: { value: "", error: "" },
    policyNumber: { value: "", error: "" },
    groupNumber: { value: "", error: "" },
  },

  address1: { value: "", error: "" },
  address2: { value: "", error: "" },
  city: { value: "", error: "" },
  state: { value: "", error: "" },
  zip: { value: "", error: "" },
  rtmSelfPay: { value: "no" as "no" | "yes", error: "" },
  rtmRedFlag: { value: "no" as "no" | "yes", error: "" },
  rtmRedFlagReason: { value: "", error: "" },
};

interface PatientScreenProps {
  socket: Socket | null;
}

const Patient: FC<PatientScreenProps> = ({ socket }) => {
  const { userDetails } = useSelector(loginSliceValue);
  const userRole = userDetails?.data?.user?.role;
  const calendarRef = useRef<HTMLInputElement>(null);

  const dispatch = useDispatch();
  const clinicId = userDetails?.data?.user?.clinic?._id || "";

  // state
  const [selectedPatient, setSelectedPatient] = React.useState<IPatient | null>(
    null,
  );
  const [showClinicianModal, setShowClinicianModal] = useState(false);
  const [currentPatientId, setCurrentPatientId] = useState<string | null>(null);
  const [showPhysicianReportModal, setShowPhysicianReportModal] = useState<{
    status: boolean;
    patient?: IPatient;
  }>({ status: false });

  const [selectedDate, setSelectedDate] = useState<{
    date: string;
    billingCycleId: string;
  } | null>(null);

  const [canEditIntakeForm, setCanEditIntakeForm] = React.useState<
    "yes" | "no" | null
  >(null);

  const [fetchingEncryptionFor, setFetchingEncryptionFor] = React.useState<
    string | null
  >(null);

  const [pageNumber, setPageNumber] = React.useState<number>(1);
  const [searchKey, setSearchKey] = React.useState<string>("");
  const [billingNumber, setBillingNumber] = React.useState<string>("");
  // const [debouncedSearchKey] = useDebounce(searchKey, 300);
  // const [debouncedBillingKey] = useDebounce(billingNumber, 300);

  const [filters, setFilters] = React.useState<Record<string, string>>(() => {
    const initial: Record<string, string> = {};
    const loggedInUserId = userDetails?.data?.user?._id;
    const loggedInUserRole = userDetails?.data?.user?.role;

    if (
      loggedInUserId &&
      loggedInUserRole !== "rtmProvider"
      // &&
      // loggedInUserRole !== "rtmManager" &&
      // loggedInUserRole !== "rtmAdmin"
    ) {
      initial.accountowner = loggedInUserId;
    }
    return initial;
  });

  const [decryptedPrivateKey, setDecryptedPrivateKey] =
    React.useState<CryptoKey | null>(null);
  const [filtersDataLoaded, setFiltersDataLoaded] = React.useState(false);

  const pagePerData = 10;
  const [sortConfig, setSortConfig] = React.useState<{
    key: string;
    direction: "asc" | "desc";
  }>({
    key: "",
    direction: "asc",
  });
  const [showChatModal, setShowChatModal] = React.useState<IChatModalState>({
    status: false,
  });

  const [showAddPatientModal, setShowAddPatientModal] =
    React.useState<AddPatientModalState>({
      status: false,
    });

  const [addPatientModalStates, setAddPatientModalStates] =
    React.useState<IaddPatientModalStates>(startaddPatientModalStates);

  const [showActiveToggleModal, setShowActiveToggleModal] =
    React.useState<IChatModalState>({});

  const [noteStates, setNoteStates] = React.useState<IAddNoteStates>({
    noteCategory: { value: "clinical", error: "" },
    noteContent: { value: "", error: "" },
  });
  const [updatedStatus, setUpdatedStatus] = React.useState(false);

  const [showFloatingTimer, setShowFloatingTimer] = useState(false);

  // hooks
  const {
    updatePatientClinician,
    fetchClinicians,
    clinicians: availableClinicians,
  } = useFetchClinicians();

  const { intakeFormStatus, getIntakeFormStatus, intakeFormStatusLoading } =
    useFetchIntakeFormStatus({
      patientId: selectedPatient?._id || "",
    });

  const { getEncryptedChatKey, encryptedChatLoading } = useEncryptedChat();

  const { associatedClinic, associatedClinicLoading, getAssociatedClinic } =
    useFetchAssociatedClinic({ clinicId });
  const { fetchingStatus, fetchStatus, status } = useFetchStatus();
  const { fetchingTransmissionType, getTransmissionType, transmissionType } =
    useFetchTransmission();

  // const { prequalifiedStatus, fetchPrequalifiedStatus, isLoading } =
  //   useFetchPrequalifiedStatus();

  const { getNotes, fetchedNotes, fetchingNotes } = useFetchNotes({
    patientId: showChatModal.data?._id,
  });

  const { addNote, addNoteLoading } = useAddNote({
    patientId: showChatModal.data?._id,
  });

  const { loadingDaysLeft, daysLeftOptions, getBillingCycleDaysLeft } =
    useFetchBillingCycleDaysLeft();

  const { alerts, getAlerts, loadingAlerts } = useGetAlerts(); // Alerts
  const { floatingTimer, getFloatingTimer } = useFloatingTimer(); // Floating Timer

  const { updatePhoneCallStatus } = useUpdatePhoneCallStatus();
  const { updatePhoneCallReminderDate } = useUpdatePhoneCallReminderDate();
  const { updateCallStatus } = useUpdateCallStatus();
  const { scheduleDateFilters, getScheduleDateFilter } =
    useGetScheduleDateFilter();

  const [callStatusByPatientId, setCallStatusByPatientId] = React.useState<
    Record<string, NonNullable<IPatient["callStatus"]>>
  >({});

  // functions
  const handleClinicianChange = async (clinicianId: string) => {
    if (!currentPatientId) return;

    try {
      const success = await updatePatientClinician(
        currentPatientId,
        clinicianId,
      );

      if (success) {
        await getpatientData();
      }
    } catch (error) {
      console.error("Error updating clinician:", error);
    } finally {
      setShowClinicianModal(false);
    }
  };

  const openClinicianModal = (patientId: string) => {
    setCurrentPatientId(patientId);
    setShowClinicianModal(true);
  };

  // hide headers for rtm provider
  const visibleHeaders = React.useMemo(() => {
    if (userRole === "rtmProvider") {
      // Filter out unwanted headers for RTM provider
      const filteredHeaders = patientTableHeadData.filter(
        (header) =>
          ![
            "Alerts",
            "Associated Clinic",
            "Account Owner",
            "Call Scheduled",
            "Monthly Intake",
          ].includes(header.title),
      );

      // Move Actions column to the end
      const actionsIndex = filteredHeaders.findIndex(
        (h) => h.title === "Actions",
      );

      if (actionsIndex !== -1) {
        const actionsHeader = filteredHeaders.splice(actionsIndex, 1)[0];
        filteredHeaders.push(actionsHeader);
      }

      return filteredHeaders;
    }
    return patientTableHeadData;
  }, [userRole]);

  useEffect(() => {
    if (userRole !== "rtmProvider") {
      getFloatingTimer();
    }
  }, []);

  // visibility when floatingTimer changes
  useEffect(() => {
    if (floatingTimer) {
      setShowFloatingTimer(floatingTimer.isAlreadyStarted === true);
    }
  }, [floatingTimer]);

  const statusMap: Record<string, string> = {
    active: "Active",
    inactive: "Inactive",
    initialized: "Initialized",
  };
  const apiStatus =
    statusMap[filters.status?.toLowerCase()] || filters.status || "";

  const { patientData, getpatientData, patientDataLoading, setPatientData } =
    useAllPatient({
      pageNumber,
      associatedClinic: filters.associatedClinic || "",
      status: apiStatus,
      transmission: filters.transmissions || "",
      billingCycleLeftDays: filters.billingCycleLeftDays || "",
      sort: sortConfig.key,
      order: sortConfig.direction,
      accountowner: filters.accountowner,
      prequalified:
        filters.prequalifiedStatus === "yes"
          ? "yes"
          : filters.prequalifiedStatus === "no"
            ? "no"
            : "",
      searchKey,
      billingCycleNumber: billingNumber,
      alerts: filters.alerts || "",
      phoneCallReminderDate: filters.phoneCallReminderDate || "",
    });

  React.useEffect(() => {
    getpatientData();
    fetchClinicians();
  }, [
    filters,
    pageNumber,
    updatedStatus,
    sortConfig,
    searchKey,
    billingNumber,
  ]);

  // Get the current clinician ID for a patient
  const getCurrentClinicianId = (patientId: string): string => {
    const patient = patientData?.data?.data?.find((p) => p._id === patientId);
    return patient?.accountOwner?._id || "";
  };

  useEffect(() => {
    if (showAddPatientModal.data?._id) {
      const formatDateForInput = (dateString: string) => {
        if (!dateString) return "";
        const [month, day, year] = dateString.split("/");
        if (month && day && year) {
          return `${year}-${month.padStart(2, "0")}-${day.padStart(2, "0")}`;
        }
        const date = new Date(dateString);
        const timezoneOffset = date.getTimezoneOffset() * 60000;
        const localDate = new Date(date.getTime() + timezoneOffset);
        return localDate.toISOString().split("T")[0];
      };
      setAddPatientModalStates({
        name: { value: showAddPatientModal.data.name || "", error: "" },
        dobString: {
          value: formatDateForInput(
            showAddPatientModal.data.dobString ||
              showAddPatientModal.data.dob ||
              "",
          ),
          error: "",
        },
        gender: { value: showAddPatientModal.data.gender || "", error: "" },
        phone: {
          value: inputPhoneMasking(showAddPatientModal.data.phone),
          error: "",
        },
        email: { value: showAddPatientModal.data.email, error: "" },
        address1: { value: showAddPatientModal.data?.address1, error: "" },
        address2: {
          value: showAddPatientModal.data?.address2 || "",
          error: "",
        },
        state: { value: showAddPatientModal.data.state, error: "" },
        city: { value: showAddPatientModal.data.city, error: "" },
        zip: { value: showAddPatientModal.data.zip, error: "" },
        //  associatedClinic: {
        //   value:
        //     showAddPatientModal.data.associatedClinic?._id ,

        //   error: "",
        // },
        // billingProvider: {
        //   value: showAddPatientModal.data.billingProvider._id,
        //   error: "",
        // },

        associatedClinic: {
          value:
            typeof showAddPatientModal.data.associatedClinic === "string"
              ? showAddPatientModal.data.associatedClinic
              : showAddPatientModal.data.associatedClinic?._id || "",
          error: "",
        },
        billingProvider: {
          value:
            typeof showAddPatientModal.data.billingProvider === "string"
              ? showAddPatientModal.data.billingProvider
              : showAddPatientModal.data.billingProvider?._id || "",
          error: "",
        },

        bestTimeToContact: {
          value: showAddPatientModal.data.bestTimeToContact || "",
          error: "",
        },
        primaryInsurance: {
          provider: {
            value: showAddPatientModal.data.primaryInsurance?.provider || "",
            error: "",
          },
          policyNumber: {
            value:
              showAddPatientModal.data.primaryInsurance?.policyNumber || "",
            error: "",
          },
          groupNumber: {
            value: showAddPatientModal.data.primaryInsurance?.groupNumber || "",
            error: "",
          },
        },
        secondaryInsurance: {
          provider: {
            value: showAddPatientModal.data.secondaryInsurance?.provider || "",
            error: "",
          },
          policyNumber: {
            value:
              showAddPatientModal.data.secondaryInsurance?.policyNumber || "",
            error: "",
          },
          groupNumber: {
            value:
              showAddPatientModal.data.secondaryInsurance?.groupNumber || "",
            error: "",
          },
        },
        rtmSelfPay: {
          value: showAddPatientModal.data?.rtmSelfPay ?? "no",
          error: "",
        },
        rtmRedFlag: {
          value: showAddPatientModal.data?.rtmRedFlag ?? "no",
          error: "",
        },
        rtmRedFlagReason: {
          value: showAddPatientModal.data?.rtmRedFlagReason || "",
          error: "",
        },
      });
    }
  }, [showAddPatientModal]);

  useEffect(() => {
    if (selectedPatient) {
      getIntakeFormStatus();
    }
  }, [selectedPatient]);

  useEffect(() => {
    if (intakeFormStatus?.data?.canEdit) {
      setCanEditIntakeForm(intakeFormStatus.data.canEdit as "yes" | "no");
    } else {
      setCanEditIntakeForm(null);
    }
  }, [intakeFormStatus]);

  let filter = [
    {
      id: "1",
      disable: false,
      label: "Associated Clinic",
      value: "associatedClinic",
      options: [
        { label: " Associated Clinic", value: "" },
        ...(associatedClinic || [])
          .slice()
          .sort((a, b) => a.name.localeCompare(b.name))
          .map((clinic) => ({
            label: clinic.name,
            value: clinic._id,
          })),
      ],
    },
    {
      id: "4",
      disable: false,
      label: "Account Owner",
      value: "accountowner",
      options: [
        { label: " Account Owner", value: "" },
        ...(availableClinicians || []).map((clinician) => ({
          label: clinician.name,
          value: clinician._id,
        })),
      ],
    },

    {
      id: "2",
      disable: false,
      label: "Status",
      value: "status",
      options: [
        { label: " Status", value: "" },
        ...(status || []).map((status) => ({
          label: status.value,
          value: status.key,
        })),
      ],
    },
    {
      id: "3",
      disable: false,
      label: "Transmissions",
      value: "transmissions",

      options: [
        { label: " Transmission", value: "" },
        ...(transmissionType || []).map((transmission) => ({
          label: transmission.value,
          value: transmission.key,
        })),
      ],
    },
    // {
    //   id: "5",
    //   disable: false,
    //   label: "Prequalified Status",
    //   value: "prequalifiedStatus",
    //   options: [
    //     { label: " Prequalified Status", value: "" },
    //     ...(prequalifiedStatus || []).map((status) => ({
    //       label: status.key,
    //       value: status.value,
    //     })),
    //   ],
    // },
    {
      id: "5",
      disable: false,
      label: "Call Scheduled",
      value: "phoneCallReminderDate",
      options: [
        { label: "Call Scheduled", value: "" },
        ...(scheduleDateFilters || []).map((status) => ({
          label: status.key,
          value: status.value,
        })),
      ],
    },
    {
      id: "6",
      disable: false,
      label: "Time Spent",
      value: "billingCycleLeftDays",
      options: [
        { label: " Time Spent", value: "" },
        ...(daysLeftOptions || []).map((option) => ({
          label: option.label,
          value: option.value,
        })),
      ],
      loading: loadingDaysLeft,
    },
    {
      id: "7",
      disable: false,
      label: "Alerts",
      value: "alerts",
      options: [
        { label: "Alerts", value: "" },
        ...(alerts || []).map((alert) => ({
          label: alert.value,
          value: alert.key,
        })),
      ],
      loading: loadingAlerts,
    },
  ];

  // hide filters for rtmProvider
  if (userRole === "rtmProvider") {
    filter = filter.filter(
      (f) =>
        ![
          "Associated Clinic",
          "Account Owner",
          "Prequalified Status",
          "Call Scheduled",
          "Alerts",
        ].includes(f.label),
    );
  }

  const handleLoadFiltersData = async () => {
    if (!filtersDataLoaded) {
      try {
        await Promise.all([
          getAssociatedClinic(),
          fetchStatus(),
          getTransmissionType(),
          // fetchPrequalifiedStatus(),
          getScheduleDateFilter(),
          getBillingCycleDaysLeft(),
          getAlerts(),
        ]);
        setFiltersDataLoaded(true);
      } catch (error) {
        console.error("Error loading filter data:", error);
      }
    }
  };

  const handleFiltersUpdate = (newFilters: Record<string, string>) => {
    setPageNumber(1);
    setFilters(newFilters);
  };

  const handleCloseChatModal = () => {
    setShowChatModal({ status: false });
  };

  const handleSearch = (searchTerm: string) => {
    setPageNumber(1);
    setSearchKey(searchTerm);
  };

  const handleBillingSearch = (searchTerm: string) => {
    setPageNumber(1);
    setBillingNumber(searchTerm);
  };

  const onPageChange = (currentPage: { selected: number }) => {
    setPageNumber(currentPage.selected + 1);
  };

  const handleCloseAddPatientModal = () => {
    setAddPatientModalStates(startaddPatientModalStates);
    dispatch(clearRescivedMessage());
    setShowAddPatientModal({ status: false });
  };

  const [expandedPatientId, setExpandedPatientId] = React.useState<
    string | null
  >(null);

  const sortedPatients = React.useMemo(() => {
    if (!patientData?.data?.data) return [];
    return [...patientData.data.data];
  }, [patientData]);

  const handleSort = (key: string) => {
    setSortConfig((prev) => {
      if (prev.key === key) {
        return { key, direction: prev.direction === "asc" ? "desc" : "asc" };
      }
      return { key, direction: "asc" };
    });
    setPageNumber(1);
  };

  const handleEyeIconClick = async (patient: IPatient) => {
    try {
      setFetchingEncryptionFor(patient._id!);

      const response = await getEncryptedChatKey(patient._id!);

      if (
        response &&
        typeof response === "object" &&
        "data" in response &&
        response.data.encryptedPrivateKey &&
        response.data.salt &&
        response.data.iv
      ) {
        const { encryptedPrivateKey, salt, iv } = response.data;

        const password = extractNumbersFromId(patient._id!);
        if (!password) throw new Error("Derived password is empty");

        const decryptedKeyBuf = await decryptPrivateKeyBackup(
          encryptedPrivateKey,
          salt,
          iv,
          password,
        );

        const importedPrivateKey = await crypto.subtle.importKey(
          "pkcs8",
          decryptedKeyBuf,
          { name: "ECDH", namedCurve: "P-256" },
          true,
          ["deriveBits"],
        );

        setDecryptedPrivateKey(importedPrivateKey);
      } else {
        console.warn("Response missing required encryption fields.");
      }

      setSelectedPatient(patient);
      setExpandedPatientId((prevVal) =>
        prevVal === patient._id ? null : patient._id!,
      );
    } catch (error) {
      console.error("Error in handleEyeIconClick:", error);
      setExpandedPatientId((prevVal) =>
        prevVal === patient._id ? null : patient._id!,
      );
    } finally {
      setFetchingEncryptionFor(null);
    }
  };

  const renderTableRow = (patient: IPatient) => {
    if (!patient) return null;
    const isExpanded = patient._id === expandedPatientId;
    const effectiveCallStatus = patient._id
      ? (callStatusByPatientId[patient._id] ?? patient.callStatus ?? "none")
      : (patient.callStatus ?? "none");
    return (
      <tr
        style={{
          backgroundColor:
            expandedPatientId === patient._id ? "#FEF2F2" : "transparent",
          fontFamily: "Roboto, sans-serif",
        }}
      >
        <td></td>
        <td
          style={{
            fontFamily: "Roboto, sans-serif",
          }}
        >
          <div
            style={{
              marginLeft: "1.5rem",
            }}
          >
            {patient.billingCycleNumber || "-"}
          </div>{" "}
        </td>

        {userRole !== "rtmProvider" && (
          <td>
            <div className={styles.patientIcons}>
              {effectiveCallStatus === "missing_documentation" && (
                <div className={styles.missingDocumentationWarningContainer}>
                  <img
                    // src="/assets/missed-call.png"
                    src="/assets/phone-call.png"
                    alt="warning"
                    className={styles.missingDocumentationWarningIcon}
                  />
                </div>
              )}
              {effectiveCallStatus === "unanswered_call" && (
                <div className={styles.unansweredCallWarningContainer}>
                  <img
                    // src="/assets/missed-call.png"
                    src="/assets/phone-call.png"
                    alt="warning"
                    className={styles.missingDocumentationWarningIcon}
                  />
                </div>
              )}
              {patient.rtmRedFlag && patient.rtmRedFlag === "yes" && (
                <div className={styles.redFlagIconContainer}>
                  <svg className={styles.redFlagIcon}>
                    <use xlinkHref="/assets/sprite.svg#icon-red-flag" />
                  </svg>
                </div>
              )}
              {patient.isTimeStarted === "yes" && (
                <img
                  src="/assets/sandClock.gif"
                  alt="timer"
                  className={styles.timerGif}
                />
              )}
              {patient.isAlertMessagesPresent === "yes" && (
                <div className={styles.patientContainer}>
                  <svg className={styles.alertIcon}>
                    <use xlinkHref="/assets/sprite.svg#icon-undelivered-red" />
                  </svg>
                </div>
              )}
              {patient.isIntakeFormInCompleted === "yes" && (
                <div className={styles.intakeFormIconContainer}>
                  <svg className={styles.intakeFormIcon}>
                    <use xlinkHref="/assets/sprite.svg#icon-exclamation" />
                  </svg>
                </div>
              )}
              {patient.isPhoneCall === "no" && (
                <div className={styles.phoneIconContainer}>
                  <img
                    src="/assets/phone.png"
                    alt="phone"
                    className={styles.phoneIcon}
                  />
                </div>
              )}

              {patient.missingAnswer === "yes" && (
                <div className={styles.missingIconContainer}>
                  <img
                    src="/assets/missingText.png"
                    alt="missing text"
                    className={styles.missingText}
                  />
                </div>
              )}
              {patient.rtmSelfPay === "yes" && (
                <div className={styles.selfPayIconContainer}>
                  <img
                    src="/assets/selfPay.jpeg"
                    alt="Self Pay"
                    className={styles.selfPayText}
                  />
                </div>
              )}
              {patient.isTimeStarted !== "yes" &&
                patient.isAlertMessagesPresent !== "yes" &&
                patient.isIntakeFormInCompleted !== "yes" &&
                patient.missingAnswer !== "yes" &&
                patient.rtmSelfPay !== "yes" &&
                patient.isPhoneCall !== "no" && (
                  <span
                    style={{
                      marginLeft: "0.5rem",
                    }}
                  >
                    -
                  </span>
                )}
            </div>
          </td>
        )}
        <td className={styles.patientNameCell}>
          <div className={styles.patientNameWrapper}>
            <div
              className="displayflex displayflexCol"
              style={{ whiteSpace: "nowrap", fontFamily: "Roboto, sans-serif" }}
            >
              <span className={styles.patientNameText}>{patient.name}</span>
              <span>{inputPhoneMasking(patient.phone)}</span>
              <span>{patient.dobString || ""}</span>
            </div>
          </div>
        </td>
        {userRole !== "rtmProvider" && (
          <td
            style={{
              fontFamily: "Roboto, sans-serif",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            {" "}
            <TableButtons
              buttons={[
                {
                  icon: "update",
                  tooltip: "Edit",
                  onClick: () =>
                    setShowAddPatientModal({ status: true, data: patient }),
                },
                {
                  icon: "toggle-eye",
                  tooltip: "View",
                  onClick: () => handleEyeIconClick(patient),
                },
              ]}
            />
          </td>
        )}

        {/* <td style={{ fontFamily: "Roboto, sans-serif" }}>
          {patient.dobString || ""}
        </td> */}

        {userRole !== "rtmProvider" && (
          <td
            style={{
              fontFamily: "Roboto, sans-serif",
              whiteSpace: "normal",
              wordWrap: "break-word",
              maxWidth: "200px",
              padding: "0.8rem 1rem",
            }}
          >
            {patient.associatedClinic?.name || "-"}
          </td>
        )}
        {userRole !== "rtmProvider" && (
          <td style={{ fontFamily: "Roboto, sans-serif" }}>
            <div className={styles.accountOwnerCell}>
              <span className={styles.accountOwnerName}>
                {patient.accountOwner?.name || "-"}
              </span>
              <TableButtons
                buttons={[
                  {
                    icon: "update",
                    tooltip: "Change Account Owner",
                    onClick: (e) => {
                      e.stopPropagation();
                      if (patient._id) {
                        openClinicianModal(patient._id);
                      }
                    },
                  },
                ]}
              />
            </div>
          </td>
        )}

        <td style={{ fontFamily: "Roboto, sans-serif" }}>
          <div
            style={{
              textAlign: userRole === "rtmProvider" ? "start" : "center",
            }}
          >{`${patient.transmission || 0}/${
            patient.requiredBillingTransmission || 0
          }`}</div>
        </td>
        <td style={{ fontFamily: "Roboto, sans-serif" }}>
          {patient.timeSpent}m
        </td>
        {userRole !== "rtmProvider" && (
          <td
            style={{
              fontFamily: "Roboto, sans-serif",
              verticalAlign: "middle",
            }}
          >
            <DatePicker
              value={formatDateForCallSchedule(
                patient.phoneCallReminderDate || "",
              )}
              onChange={async (dateString) => {
                if (!patient._id) return;

                try {
                  const [year, month, day] = dateString.split("-");

                  const response = await updatePhoneCallReminderDate(
                    patient._id,
                    {
                      phoneCallReminderDate: `${month}/${day}/${year}`,
                    },
                  );

                  if (response?.data) {
                    setPatientData((prev) => {
                      if (!prev) return prev;
                      const updatedPatients = prev.data.data.map((p) =>
                        p._id === patient._id
                          ? {
                              ...p,
                              phoneCallReminderDate:
                                response.data?.phoneCallReminderDate,
                            }
                          : p,
                      );
                      return {
                        ...prev,
                        data: {
                          ...prev.data,
                          data: updatedPatients,
                        },
                      };
                    });
                  }
                } catch (error) {
                  console.error("Failed to update reminder date", error);
                }
              }}
              minDate={new Date().toISOString().split("T")[0]}
              inputStyle={{
                border: "none",
                boxShadow: "none",
                background: "transparent",
                fontSize: "1.25rem",
              }}
            />
          </td>
        )}

        {userRole !== "rtmProvider" && (
          <td style={{ fontFamily: "Roboto, sans-serif" }}>
            <div style={{ display: "flex", justifyContent: "center" }}>
              <CheckboxInput
                checkboxStyle={{
                  accentColor: "#0f407a",
                  width: "18px",
                  height: "18px",
                  cursor: "pointer",
                }}
                checked={patient.isPhoneCall === "yes"}
                onChange={async (e) => {
                  if (!patient._id) return;
                  const newStatus = e.target.checked ? "yes" : "no";
                  if (patientData) {
                    setPatientData((prev: any) => {
                      if (!prev) return prev;
                      const updatedPatients = prev.data.data.map((p: any) =>
                        p._id === patient._id
                          ? { ...p, isPhoneCall: newStatus }
                          : p,
                      );
                      return {
                        ...prev,
                        data: {
                          ...prev.data,
                          data: updatedPatients,
                        },
                      };
                    });
                  }

                  try {
                    await updatePhoneCallStatus(patient._id, {
                      status: newStatus,
                    });
                  } catch (error) {
                    console.error("Failed to update phone call status:", error);
                    // Revert on error
                    if (patientData) {
                      setPatientData((prev: any) => {
                        if (!prev) return prev;
                        const revertedPatients = prev.data.data.map((p: any) =>
                          p._id === patient._id
                            ? {
                                ...p,
                                isPhoneCall: newStatus === "yes" ? "no" : "yes",
                              }
                            : p,
                        );
                        return {
                          ...prev,
                          data: {
                            ...prev.data,
                            data: revertedPatients,
                          },
                        };
                      });
                    }
                  }
                }}
                hideCheckbox={false}
              />
            </div>
          </td>
        )}
        {/* <td style={{ fontFamily: "Roboto, sans-serif" }}>
          <RadioDropdown
            value={
              patient._id
                ? (callStatusByPatientId[patient._id] ??
                  patient.callStatus ??
                  "none")
                : (patient.callStatus ?? "none")
            }
            onChange={async (status) => {
              if (!patient._id) return;

              const patientId = patient._id;
              const previousValue = patient.callStatus ?? "none";
              setCallStatusByPatientId((prev) => ({
                ...prev,
                [patientId]: status,
              }));

              try {
                const response = await updateCallStatus(patientId, {
                  status: status,
                });

                const updatedCallStatus = response?.data?.callStatus;
                if (updatedCallStatus) {
                  setCallStatusByPatientId((prev) => ({
                    ...prev,
                    [patientId]: updatedCallStatus,
                  }));
                }

                if (response?.data) {
                  setPatientData((prev) => {
                    if (!prev) return prev;
                    const updatedPatients = prev.data.data.map((p) =>
                      p._id === patientId
                        ? {
                            ...p,
                            callStatus: updatedCallStatus ?? status,
                          }
                        : p,
                    );

                    return {
                      ...prev,
                      data: {
                        ...prev.data,
                        data: updatedPatients,
                      },
                    };
                  });
                }
                await getpatientData();
              } catch (error) {
                console.error("Failed to update call status", error);

                // Revert optimistic UI on failure.
                setCallStatusByPatientId((prev) => ({
                  ...prev,
                  [patientId]: previousValue,
                }));
              }
            }}
            options={[
              {
                label: "Missing Data",
                value: "missing_documentation",
              },
              { label: "Unanswered", value: "unanswered_call" },
              { label: "None", value: "none" },
            ]}
          />
        </td> */}

        <td style={{ fontFamily: "Roboto, sans-serif" }}>
          {patient.enrollDate
            ? formatDateToMMDDYYYY(new Date(patient.enrollDate))
            : "-"}
        </td>
        {/* Days Left column commented out as per request
        <td style={{ fontFamily: "Roboto, sans-serif" }}>
          {patient.rtmStatus === "Inactive"
            ? "-"
            : ${patient.leftDays || 0}/${patient.requiredBillingDays || 0}}
        </td>
        */}
        <td style={{ fontFamily: "Roboto, sans-serif" }}>
          {userRole === "rtmProvider" ? (
            <div style={{ display: "flex", alignItems: "center" }}>
              <p
                className={
                  patient.rtmStatus === "Active"
                    ? classes.table__status_active
                    : patient.rtmStatus === "Pending"
                      ? classes.table__status_pending
                      : classes.table__status_deactive
                }
              >
                {patient.rtmStatus}
              </p>
            </div>
          ) : (
            <TableStatusWithToggle
              vertical
              toggleCustomStyle={{
                cursor: "pointer",
                opacity: patient.rtmStatus === "Pending" ? 0.8 : 1,
              }}
              id={patient._id}
              status={
                patient.rtmStatus === "Active"
                  ? "Active"
                  : patient.rtmStatus === "Pending"
                    ? "Pending"
                    : "Inactive"
              }
              disabled={false}
              checked={patient.rtmStatus === "Active"}
              onChange={() => {
                setShowActiveToggleModal({
                  status: true,
                  data: patient,
                });
              }}
            />
          )}
        </td>

        {userRole === "rtmProvider" && (
          <div
            style={{
              marginLeft: "1.5rem",
              marginTop: "2rem",
            }}
          >
            <TableButtons
              buttons={[
                {
                  icon: "pdf-icon-1",
                  tooltip: "View Physician Report",
                  onClick: (e) => {
                    e.stopPropagation();
                    setShowPhysicianReportModal({
                      status: true,
                      patient: patient,
                    });
                  },
                },
              ]}
            />
          </div>
        )}
      </tr>
    );
  };

  const handleGetNotes = async (
    reset?: boolean,
    sortOrder?: "asc" | "desc",
  ) => {
    if (reset) {
      await getNotes(true, sortOrder);
    } else {
      await getNotes(false, sortOrder);
    }
  };

  // const handleSubmit = async (
  //   event: React.FormEvent<HTMLFormElement>
  // ): Promise<{ status: string; data?: any } | undefined> => {
  //   event.preventDefault();

  //   try {
  //     const formData = new FormData(event.currentTarget);
  //     const noteContent = formData.get("noteContent") as string;
  //     const noteCategory =
  //       (formData.get("noteCategory") as "clinical" | "non-clinical") ||
  //       "clinical";
  //     const isImportant = formData.get("isImportant") === "yes" ? "yes" : "no";
  //     const noteType =
  //       (formData.get("noteType") as string) ||
  //       (formData.get("type") as string) ||
  //       "general-note";

  //     console.log("Received form data:", {
  //       noteContent,
  //       noteCategory,
  //       isImportant,
  //       noteType,
  //       allFormData: Object.fromEntries(formData.entries()),
  //     });

  //     if (!noteContent?.trim()) {
  //       setNoteStates((prevVal) => ({
  //         ...prevVal,
  //         noteContent: {
  //           ...prevVal.noteContent,
  //           error: "Note content cannot be empty",
  //         },
  //       }));
  //       return;
  //     }

  //     const tempDiv = document.createElement("div");
  //     tempDiv.innerHTML = noteContent;
  //     const plainText = tempDiv.textContent || tempDiv.innerText || "";

  //     const noteData: any = {
  //       note: plainText,
  //       richText: noteContent,
  //       noteCategory,
  //       isImportant,
  //     };

  //     if (noteCategory === "clinical") {
  //       noteData.type = noteType;
  //     }

  //     const response = await addNote(noteData);

  //     if (response?.status === "success") {
  //       await handleGetNotes(true, "desc");
  //       setNoteStates({
  //         noteCategory: { value: noteCategory, error: "" },
  //         noteContent: { value: "", error: "" },
  //       });
  //       return response; // Return the response to be used by the AddNotesModal
  //     }
  //   } catch (error: any) {
  //     console.error("Error adding note:", error);
  //   }
  // };

  const handleRefreshOnStatusChange = () => {
    setUpdatedStatus((prev) => !prev);
  };

  const handleIntakeFormUpdate = () => {
    if (selectedPatient) {
      setPatientData((prev) => {
        if (!prev) return prev;
        const updatedPatients = prev.data.data.map((patient) => {
          if (patient._id === selectedPatient._id) {
            return {
              ...patient,
              isIntakeFormInCompleted: "no" as "yes" | "no",
            };
          }
          return patient;
        });
        return {
          ...prev,
          data: {
            ...prev.data,
            data: updatedPatients,
          },
        };
      });
    }

    getIntakeFormStatus();
  };
  return (
    <Content currentMenu="patient" list={true}>
      <ListHeader
        showFilter
        title="Patient List"
        searchPlaceholder="Search by name, email, phone, or DOB"
        filterOptions={filter}
        actionButtonText={
          userRole !== "rtmProvider" ? "Enroll Patient" : undefined
        }
        onActionButtonClick={() => {
          setShowAddPatientModal({ status: true });
        }}
        filters={filters}
        setFilters={handleFiltersUpdate}
        onSearchChange={handleSearch}
        style={{ fontFamily: "Roboto, sans-serif" }}
        onBillingSearchChange={handleBillingSearch}
        onFilterButtonClick={handleLoadFiltersData}
        showBillingCycle={true}
      />
      <Table className={classes.patientTable}>
        <TableHead
          data={visibleHeaders}
          onSort={handleSort}
          sortKey={sortConfig?.key || ""}
          sortOrder={sortConfig?.direction || "asc"}
          style={{ fontFamily: "Roboto, sans-serif" }}
          rowStyle={{ borderBottom: "none" }}
        />

        <TableBody>
          {patientDataLoading ? (
            <CustomTableRow>
              <TableSkeleton rows={14} columns={10} />
            </CustomTableRow>
          ) : patientData && sortedPatients.length > 0 ? (
            sortedPatients.map((patient: IPatient) => (
              <React.Fragment key={patient._id}>
                {renderTableRow(patient)}
                {patient._id === expandedPatientId ? (
                  <tr>
                    <td colSpan={15} style={{ padding: 0, border: "none" }}>
                      <div style={{ overflow: "hidden" }}>
                        <AccordionRow
                          patient={patient}
                          isOpen={true}
                          onTimerStart={() => setShowFloatingTimer(true)}
                          onTimerStop={() => setShowFloatingTimer(false)}
                          getFloatingTimer={getFloatingTimer}
                          onClose={() => setExpandedPatientId(null)}
                          onSave={(data: IPatient) => {
                            setPatientData((prev) => {
                              if (!prev) return prev;
                              const newPatientData = prev.data.data.map((p) =>
                                p._id === data._id ? data : p,
                              );
                              return {
                                ...prev,
                                data: { ...prev.data, data: newPatientData },
                              };
                            });
                          }}
                          decryptPrivateKeyData={decryptedPrivateKey}
                          patientData={patientData}
                          setPatientData={setPatientData}
                          canEdit={
                            canEditIntakeForm !== null
                              ? canEditIntakeForm
                              : undefined
                          }
                          intakeFormStatusLoading={intakeFormStatusLoading}
                          onIntakeFormUpdate={handleIntakeFormUpdate}
                          getPatientData={getpatientData}
                        />
                      </div>
                    </td>
                  </tr>
                ) : null}
              </React.Fragment>
            ))
          ) : (
            <CustomTableRow>
              <TableNoContent
                colSpan={patientTableHeadData.length}
                message="No data found"
              />
            </CustomTableRow>
          )}
        </TableBody>
      </Table>

      <Pagination
        itemsPerPage={pagePerData}
        totalItems={patientData ? patientData.totalResults : 0}
        onPageChange={onPageChange}
        currentPage={pageNumber}
      />

      {showChatModal.status && (
        <ChatModal
          patientData={showChatModal.data}
          status={showChatModal.status}
          onClose={handleCloseChatModal}
        />
      )}

      {/* {showAddPatientModal.status && (
        <AddPatientModal
          loading={patientDataLoading}
          addEditModal={showAddPatientModal}
          onCloseModal={handleCloseAddPatientModal}
          addEditModalStates={addPatientModalStates}
          setAddEditModalStates={setAddPatientModalStates}
          addPatientModalStates={addPatientModalStates}
          setAddPatientModalStates={setAddPatientModalStates}
          getPatientData={getpatientData}
          socket={socket}
          patientData={patientData}
          setPatientData={setPatientData}
        />
      )} */}

      {showAddPatientModal.status && (
        <EnrollPatientModal
          loading={patientDataLoading}
          addEditModal={showAddPatientModal}
          onCloseModal={handleCloseAddPatientModal}
          addEditModalStates={addPatientModalStates}
          setAddEditModalStates={setAddPatientModalStates}
          addPatientModalStates={addPatientModalStates}
          setAddPatientModalStates={setAddPatientModalStates}
          getPatientData={getpatientData}
          socket={socket}
          patientData={patientData}
          setPatientData={setPatientData}
        />
      )}

      {/* <AddNotesModal
        addNotesModal={showNotesModal}
        onCloseModal={() => setShowNotesModal(false)}
        patientData={showChatModal.data}
        noteModalStates={noteStates}
        setNoteModalStates={setNoteStates}
        handleSubmit={handleSubmit}
        getNotes={handleGetNotes}
        fetchedNotes={fetchedNotes}
        getNotesLoading={fetchingNotes}
      /> */}

      {showActiveToggleModal.status && (
        <ActiveToggleModal
          show
          onClose={() => setShowActiveToggleModal({ status: false })}
          onSave={handleRefreshOnStatusChange}
          isActive={showActiveToggleModal.data?.rtmStatus === "Active"}
          patient={showActiveToggleModal.data as IPatient}
        />
      )}

      {showClinicianModal && currentPatientId && (
        <SelectClinicianModal
          show={showClinicianModal}
          onClose={() => setShowClinicianModal(false)}
          onSelect={handleClinicianChange}
          clinicians={availableClinicians}
          currentClinicianId={getCurrentClinicianId(currentPatientId)}
        />
      )}

      {showPhysicianReportModal.status && showPhysicianReportModal.patient && (
        <PreviewModalVersion2
          show={true}
          onClose={() => {
            setShowPhysicianReportModal({ status: false });
            setSelectedDate(null);
          }}
          setSelectedDate={setSelectedDate}
          selectedDate={selectedDate}
          patient={showPhysicianReportModal.patient}
        />
      )}

      {showFloatingTimer && floatingTimer && (
        <FloatingTimer floatingTimer={floatingTimer as TimerResponse} />
      )}
    </Content>
  );
};

export default Patient;
